import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { Location } from "react-router-dom";

interface NavigationState {
  currentModule: string | null;
  currentPath: string | null;
  navigationHistory: Array<{ module: string; path: string }>;
}

class RoutingService {
  private readonly MAX_HISTORY_SIZE = 10;
  private _state$ = new BehaviorSubject<NavigationState>({
    currentModule: null,
    currentPath: null,
    navigationHistory: [],
  });

  public state$ = this._state$.asObservable();

  public currentModule$ = this._state$.pipe(
    map((state) => state.currentModule),
    distinctUntilChanged(),
  );

  public updateModuleNavigation(moduleName: string, location: Location) {
    const fullPath = `${location.pathname}${location.search}`;
    const current = this._state$.value;
    const { navigationHistory } = current;

    const lastHistoryEntry = navigationHistory[navigationHistory.length - 1];
    const isNavigatingBack = lastHistoryEntry?.module === moduleName;

    if (isNavigatingBack) {
      this._state$.next({
        ...current,
        currentModule: moduleName,
        currentPath: fullPath,
        navigationHistory: navigationHistory.slice(0, -1),
      });
    } else if (current.currentModule && current.currentModule !== moduleName) {
      const newHistory = [
        ...navigationHistory,
        { module: current.currentModule, path: current.currentPath! },
      ].slice(-this.MAX_HISTORY_SIZE);

      this._state$.next({
        currentModule: moduleName,
        currentPath: fullPath,
        navigationHistory: newHistory,
      });
    } else {
      this._state$.next({
        ...current,
        currentModule: moduleName,
        currentPath: fullPath,
      });
    }
  }

  public getModuleReferrerPath(): string | null {
    const { navigationHistory } = this._state$.value;
    if (!navigationHistory.length) return null;

    const previousEntry = navigationHistory[navigationHistory.length - 1];
    return previousEntry?.path ?? null;
  }
}

let _routingService: RoutingService;

export const getRoutingService = () => {
  if (!_routingService) {
    _routingService = new RoutingService();
  }
  return _routingService;
};
